<template>
  <div class="stores-selection-page">
    <!-- Loading -->
    <div
      v-if="loading"
      class="loading-container"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
      <p class="mt-3 text-muted">
        Carregando lojas...
      </p>
    </div>

    <!-- Seleção de Lojas -->
    <div
      v-else-if="storesList.length > 1"
      class="stores-container"
    >
      <div class="stores-header">
        <h2 class="mb-1">
          Gerenciar Campanhas
        </h2>
        <p class="text-muted mb-0">
          Selecione a loja para gerenciar suas campanhas
        </p>
      </div>

      <div class="stores-grid">
        <div
          v-for="store in storesList"
          :key="store.uuid"
          class="store-card"
          @click="selecionarLoja(store)"
        >
          <div
            class="store-avatar"
            :style="getAvatarStyle(store)"
          >
            <span v-if="!store.logo">{{ getInitials(store.name) }}</span>
            <img
              v-else
              :src="store.logo"
              :alt="store.name"
            >
          </div>
          <div class="store-info">
            <h5 class="store-name">
              {{ store.name }}
            </h5>
            <p class="store-description text-muted w-100">
              Selecione a loja para gerenciar suas campanhas
            </p>
          </div>
          <div class="store-action mt-0">
            <feather-icon
              icon="ArrowRightIcon"
              size="18"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Nenhuma loja -->
    <div
      v-else-if="storesList.length === 0 && !loading"
      class="empty-container"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="64"
        class="text-warning mb-3"
      />
      <h4>Nenhuma loja encontrada</h4>
      <p class="text-muted">
        Você precisa ter pelo menos uma loja configurada para gerenciar campanhas.
      </p>
      <b-button
        variant="primary"
        @click="$router.push({ name: 'stores' })"
      >
        <feather-icon
          icon="PlusIcon"
          size="16"
          class="mr-50"
        />
        Configurar Loja
      </b-button>
    </div>
  </div>
</template>

<script>
import { BSpinner, BBadge, BButton } from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import stores from '@/functions/basics/stores'

export default {
  name: 'CampanhasOficiaisRedirect',
  components: {
    BSpinner,
    BBadge,
    BButton,
    FeatherIcon,
  },
  data() {
    return {
      loading: true,
      storesList: [],
    }
  },
  async mounted() {
    try {
      this.storesList = await stores() || []

      // Se houver apenas uma loja, redireciona direto
      if (this.storesList.length === 1) {
        this.$router.replace({
          name: 'campaign-oficiais',
          params: { uuid: this.storesList[0].uuid },
        })
        return
      }

      this.loading = false
    } catch (error) {
      console.error('Erro ao carregar lojas:', error)
      this.loading = false
      this.$bvToast.toast('Erro ao carregar lojas. Tente novamente.', {
        title: 'Erro',
        variant: 'danger',
        solid: true,
      })
    }
  },
  methods: {
    selecionarLoja(store) {
      this.$router.push({
        name: 'campaign-oficiais',
        params: { uuid: store.uuid },
      })
    },

    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    },

    getAvatarStyle(store) {
      const colors = [
        '#9A3092', '#28C76F', '#FF9F43', '#00CFE8', '#EA5455',
        '#7C3A8E', '#B84BAF', '#6B2D68', '#D35CC9', '#A14199',
      ]
      const index = store.name ? store.name.charCodeAt(0) % colors.length : 0
      return {
        backgroundColor: store.logo ? 'transparent' : colors[index],
      }
    },
  },
}
</script>

<style scoped>
.stores-selection-page {
  min-height: 70vh;
  padding: 2rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}

.stores-container {
  max-width: 1000px;
  margin: 0 auto;
}

.stores-header {
  text-align: center;
  margin-bottom: 2rem;
}

.header-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: rgba(154, 48, 146, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.stores-header h2 {
  color: #2c3e50;
  font-weight: 600;
}

.stores-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
}

.store-card {
  background: white;
  border: 2px solid #e8e8e8;
  border-radius: 16px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  min-height: 200px;
}

.store-card:hover {
  border-color: #9a3092;
  box-shadow: 0 8px 24px rgba(154, 48, 146, 0.15);
  transform: translateY(-4px);
}

.store-avatar {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
  overflow: hidden;
  margin-bottom: 1rem;
}

.store-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.store-info {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.store-name {
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.store-description {
  font-size: 0.85rem;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  line-height: 1.4;
}

.store-meta {
  margin-top: 1rem;
}

.store-action {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(154, 48, 146, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9a3092;
  opacity: 0;
  transform: translateX(-8px);
  transition: all 0.3s ease;
}

.store-card:hover .store-action {
  opacity: 1;
  transform: translateX(0);
}

.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;
  padding: 2rem;
}

.empty-container h4 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.empty-container p {
  max-width: 400px;
  margin-bottom: 1.5rem;
}

@media (max-width: 768px) {
  .stores-selection-page {
    padding: 1rem;
  }

  .stores-grid {
    grid-template-columns: 1fr;
  }

  .store-card {
    min-height: auto;
    padding: 1.25rem;
  }

  .store-avatar {
    width: 60px;
    height: 60px;
    font-size: 1.25rem;
  }
}

.icon-primary {
  color: #9a3092;
}
</style>
